import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthProvider';

/**
 * The "access not provisioned" screen — Phase 1 step 7.
 *
 * Shown to an AUTHENTICATED user carrying no recognized app role
 * (403 NO_ROLE_ASSIGNED). Whether anyone ever sees it depends on a tenant
 * setting nobody has confirmed yet (§8d, "five things to verify" item 5):
 *
 *  - "Assignment required?" ON  — Entra stops the user first and this screen
 *    is unreachable, a pure backstop;
 *  - "Assignment required?" OFF — the user reaches us with no `roles` claim
 *    and this is what they see.
 *
 * Both fail closed; only the experience differs. It is built for real use
 * because the cheaper assumption is the one that leaves someone staring at a
 * blank page.
 */
export function AccessNotProvisioned({ email }: { email?: string }): React.ReactElement {
  const { signOut } = useAuth();

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-lg space-y-4">
        <h1 className="text-xl font-semibold">Access not provisioned</h1>
        <p className="text-sm text-muted-foreground">
          You are signed in{email ? ` as ${email}` : ''}, but your account has not been granted
          access to the Menlo funnel.
        </p>
        <p className="text-sm text-muted-foreground">
          Access is managed by membership of a security group — Funnel-Rep, Funnel-FAE,
          Funnel-SalesManager, Funnel-Exec or Funnel-Admin. Ask IT to add you to the group that
          matches your role. Roles are assigned in Entra and nowhere else.
        </p>
        <Button variant="outline" onClick={signOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
